import type { ReactNode } from "react";

export interface TableColumn<T> {
  key: string;
  label: string;
  width?: number | string;
  align?: "left" | "right" | "center";
  render?: (row: T) => ReactNode;
}

interface ReportTableProps<T> {
  columns: Array<TableColumn<T>>;
  rows: T[];
  rowKey: (row: T, idx: number) => string;
  onRowClick?: (row: T) => void;
  empty?: string;
}

export function ReportTable<T>({
  columns,
  rows,
  rowKey,
  onRowClick,
  empty = "No data",
}: ReportTableProps<T>) {
  if (rows.length === 0) {
    return (
      <div className="py-12 text-center text-sm text-muted-foreground">
        {empty}
      </div>
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[13px]" style={{ borderCollapse: "collapse" }}>
        <thead className="bg-[hsl(210,30%,97%)] text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">
          <tr>
            {columns.map((c) => (
              <th
                key={c.key}
                className="px-[14px] py-[9px]"
                style={{
                  width: c.width,
                  textAlign: c.align ?? "left",
                }}
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {rows.map((r, idx) => (
            <tr
              key={rowKey(r, idx)}
              className={
                onRowClick
                  ? "cursor-pointer transition-colors hover:bg-[hsl(210,50%,98%)]"
                  : ""
              }
              onClick={onRowClick ? () => onRowClick(r) : undefined}
            >
              {columns.map((c) => {
                const val = c.render ? c.render(r) : (r as unknown as Record<string, ReactNode>)[c.key];
                return (
                  <td
                    key={c.key}
                    className="px-[14px] py-[10px]"
                    style={{ textAlign: c.align ?? "left" }}
                  >
                    {val ?? "—"}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
